import PassportItem from '@/components/Record/SubjectContent/PassportItem';
import React, { useEffect, useRef } from 'react';
import {
  InternalParticipant,
  PassportKey,
  RecordFilter,
  TalkFlowShare,
} from '@/types/api';
import { StyledAllLabel } from './styles';
import { Controller, useFormContext } from 'react-hook-form';
import ListContainer from '@/components/Record/SubjectContent/ListContainer';
import { t } from 'i18next';
import CustomCheckBox from '@/components/Record/CustomCheckBox';

type P = {
  registerKey: PassportKey; // innerAttender | shareAttender
  open: boolean;
  data?: InternalParticipant[];
  disableData?: TalkFlowShare[];
  focusIndex?: number;
  onToggle: (passport: InternalParticipant) => void;
  searchKeyword?: string;
};

const SearchList = ({
  open,
  data,
  registerKey,
  disableData,
  focusIndex = -1,
  onToggle,
  searchKeyword,
}: P) => {
  const { control } = useFormContext<RecordFilter>();

  const disableContents = disableData?.map((res) => res.scope.value);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusIndex >= 0 && scrollRef.current && data && data.length > 0) {
      // 이제 구조가 단순해졌으므로 직접 접근
      const focusedElement = scrollRef.current.children[
        focusIndex
      ] as HTMLElement;

      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [focusIndex, data]);

  if (!open) return null;

  return (
    <Controller
      name={registerKey}
      control={control}
      render={({ field }) => {
        const selectedList: InternalParticipant[] = field.value ?? [];

        const isIn = (passportId: string) =>
          selectedList.some((p) => p.passportId === passportId);

        const selectableData = data?.filter(
          (p) => !disableContents?.includes(p.passportId),
        );

        const isAllIn = selectableData?.length
          ? selectableData.every((p) => isIn(p.passportId))
          : false;

        const handleToggle = (passport: InternalParticipant) => {
          onToggle(passport);
        };

        const handleToggleAll = () => {
          if (!data) return;

          const updated = isAllIn
            ? selectedList.filter(
                (p) => !data.some((d) => d.passportId === p.passportId),
              )
            : [
                ...selectedList,
                ...data
                  .filter((p) => !isIn(p.passportId))
                  .filter((p) => !disableContents?.includes(p.passportId)),
              ];

          field.onChange(updated);
        };

        return (
          <ListContainer
            header={
              <StyledAllLabel onClick={handleToggleAll}>
                <CustomCheckBox
                  checked={isAllIn}
                  style={{ marginRight: '8px' }}
                />
                {t('selectAllWithResults', {
                  count: data?.length ?? 0,
                  formattedCount: data?.length ?? '-',
                  defaultValue: `Select all (${data?.length ?? '-'}) result(s)`,
                })}
              </StyledAllLabel>
            }
            content={
              <>
                {data?.map((passport, index) => (
                  <PassportItem
                    key={passport.passportId}
                    passport={passport}
                    handleChange={() => handleToggle(passport)}
                    isChecked={
                      isIn(passport.passportId) ||
                      disableContents?.includes(passport.passportId)
                    }
                    disabled={disableContents?.includes(passport.passportId)}
                    isFocused={focusIndex === index}
                    searchKeyword={searchKeyword}
                  />
                ))}
              </>
            }
            contentRef={scrollRef}
          ></ListContainer>
        );
      }}
    />
  );
};

export default SearchList;
